import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  UserCog, Plus, Mail, Phone, MapPin, Calendar, Clock, Briefcase,
  Edit2, X, Save, ChevronRight, Star, Award, DollarSign, Cake,
  FileText, Shield, TrendingUp, RefreshCw, Trash2, CalendarClock, KeyRound, Check,
  Eye, EyeOff, Copy, CheckCircle2, LogOut
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { BulkServicesForStaffDialog } from "../components/BulkServicesForStaffDialog";

const DAY_LABELS = [
  { idx: 0, short: "Mon", long: "Monday" },
  { idx: 1, short: "Tue", long: "Tuesday" },
  { idx: 2, short: "Wed", long: "Wednesday" },
  { idx: 3, short: "Thu", long: "Thursday" },
  { idx: 4, short: "Fri", long: "Friday" },
  { idx: 5, short: "Sat", long: "Saturday" },
  { idx: 6, short: "Sun", long: "Sunday" },
];

function minutesFromHHMM(s) {
  const [h, m] = (s || "0:0").split(":").map(Number);
  return h * 60 + (m || 0);
}

function formatTime12(s) {
  if (!s) return "";
  const [h, m] = s.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

export function ScheduleTab({ userId, locations }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  // `adding` is the day-of-week index that triggered the form; null = closed.
  const [adding, setAdding] = useState(null);
  // `editing` holds the shift being edited (inline replacement for `adding`).
  const [editing, setEditing] = useState(null);
  // repeat_days: set of day_of_week ints the new shift should be cloned onto.
  // Defaults to just the clicked day; user can tick more chips to repeat.
  const [form, setForm] = useState({ location_id: "", start_time: "09:00", end_time: "17:00", repeat_days: [] });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/staff/${userId}/schedule`);
      setShifts(res.data || []);
    } catch { toast.error("Couldn't load schedule"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  useEffect(() => {
    if (locations?.length && !form.location_id) {
      setForm(f => ({ ...f, location_id: locations[0].location_id }));
    }
  }, [locations, form.location_id]);

  const save = async () => {
    if (!form.location_id) { toast.error("Pick a location"); return; }
    if (minutesFromHHMM(form.end_time) <= minutesFromHHMM(form.start_time)) {
      toast.error("End time must be after start time"); return;
    }
    // repeat_days always contains the triggering day + any chips the user ticked.
    const days = Array.from(new Set([adding, ...(form.repeat_days || [])])).filter(d => d !== null && d !== undefined);
    try {
      if (days.length === 1) {
        // Single-day create keeps the original tighter endpoint for clarity.
        await api.post(`/staff/${userId}/schedule`, {
          location_id: form.location_id,
          day_of_week: days[0],
          start_time: form.start_time,
          end_time: form.end_time,
        });
        toast.success("Shift added — repeats every week");
      } else {
        const res = await api.post(`/staff/${userId}/schedule/bulk`, {
          location_id: form.location_id,
          days_of_week: days,
          start_time: form.start_time,
          end_time: form.end_time,
        });
        const created = res.data?.created || 0;
        const dups = res.data?.duplicates || [];
        if (dups.length) {
          toast.success(`${created} shifts added. Skipped ${dups.join(", ")} — already scheduled.`);
        } else {
          toast.success(`${created} shifts added — repeats every week`);
        }
      }
      setAdding(null);
      setForm(f => ({ ...f, repeat_days: [] }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (s) => {
    setAdding(null);
    setEditing({
      schedule_id: s.schedule_id,
      day_of_week: s.day_of_week,
      location_id: s.location_id,
      start_time: s.start_time,
      end_time: s.end_time,
    });
  };

  const saveEdit = async () => {
    if (minutesFromHHMM(editing.end_time) <= minutesFromHHMM(editing.start_time)) {
      toast.error("End time must be after start time"); return;
    }
    try {
      await api.put(`/schedule/${editing.schedule_id}`, {
        location_id: editing.location_id,
        day_of_week: editing.day_of_week,
        start_time: editing.start_time,
        end_time: editing.end_time,
      });
      toast.success("Shift updated");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save shift"); }
  };

  const remove = async (sid) => {
    try {
      await api.delete(`/schedule/${sid}`);
      toast.success("Shift removed");
      setShifts(prev => prev.filter(s => s.schedule_id !== sid));
    } catch { toast.error("Failed to remove"); }
  };

  const byDay = {};
  shifts.forEach(s => { (byDay[s.day_of_week] = byDay[s.day_of_week] || []).push(s); });

  const locName = (id) => locations.find(l => l.location_id === id)?.name || "—";
  const locColor = (id) => {
    const palette = ["#6366F1", "#EC4899", "#14B8A6", "#F59E0B", "#8B5CF6", "#10B981"];
    const idx = locations.findIndex(l => l.location_id === id);
    return palette[idx % palette.length] || "#64748B";
  };

  const weeklyMinutes = shifts.reduce((sum, s) => sum + Math.max(0, minutesFromHHMM(s.end_time) - minutesFromHHMM(s.start_time)), 0);
  const weeklyHours = (weeklyMinutes / 60).toFixed(1);
  const perLocation = {};
  shifts.forEach(s => {
    const m = Math.max(0, minutesFromHHMM(s.end_time) - minutesFromHHMM(s.start_time));
    perLocation[s.location_id] = (perLocation[s.location_id] || 0) + m;
  });

  return (
    <div data-testid="schedule-tab" className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-xl p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 font-['Manrope']">Weekly Hours</p>
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">{weeklyHours}<span className="text-base text-slate-400 font-normal">h</span></p>
        </div>
        <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Shifts / Week</p>
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">{shifts.length}</p>
        </div>
      </div>

      {/* Weekly-recurring explainer — eliminates the "does this repeat?" confusion */}
      <div className="rounded-xl bg-[#FAFAFA] border border-slate-200 px-3 py-2 flex items-start gap-2" data-testid="schedule-recurring-hint">
        <RefreshCw className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
        <p className="text-[11px] text-slate-500 font-['Manrope'] leading-relaxed">
          Every shift here repeats weekly on the same day. To set 9 am – 5 pm Mon through Fri in one shot, click <span className="font-semibold text-slate-700">+ Shift</span> on any day and pick the other days below "Repeat on".
        </p>
      </div>

      {Object.keys(perLocation).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="per-location-totals">
          {Object.entries(perLocation).map(([lid, mins]) => (
            <span key={lid} className="text-[11px] font-semibold px-2.5 py-1 rounded-full font-['Manrope']"
              style={{ backgroundColor: `${locColor(lid)}15`, color: locColor(lid) }}>
              {locName(lid)} — {(mins / 60).toFixed(1)}h
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-400">Loading schedule…</p>
      ) : (
        <div className="space-y-2">
          {DAY_LABELS.map(d => (
            <div key={d.idx} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`schedule-day-${d.idx}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-[#FAFAFA]">
                <p className="text-xs font-semibold text-slate-900 uppercase tracking-wider font-['Manrope']">{d.long}</p>
                <Button variant="ghost" size="sm" onClick={() => { setAdding(d.idx); setEditing(null); setForm(f => ({ ...f, start_time: "09:00", end_time: "17:00", repeat_days: [] })); }}
                  data-testid={`add-shift-${d.idx}`}
                  className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-900">
                  <Plus className="w-3 h-3 mr-0.5" /> Shift
                </Button>
              </div>
              <div className="p-3 space-y-1.5">
                {(byDay[d.idx] || []).length === 0 && adding !== d.idx && (
                  <p className="text-[11px] text-slate-400 font-['Manrope'] italic">Off</p>
                )}
                {(byDay[d.idx] || []).map(s => (
                  editing?.schedule_id === s.schedule_id ? (
                    <div key={s.schedule_id} className="rounded-lg border border-indigo-300 bg-indigo-50/30 p-3 space-y-2" data-testid={`edit-shift-form-${s.schedule_id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 font-['Manrope']">Editing {DAY_LABELS[d.idx].long} shift</p>
                      </div>
                      <Select value={editing.location_id} onValueChange={v => setEditing(p => ({ ...p, location_id: v }))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Start</label>
                          <Input type="time" value={editing.start_time} onChange={e => setEditing(p => ({ ...p, start_time: e.target.value }))} className="h-8 text-sm" data-testid={`edit-shift-start-${s.schedule_id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">End</label>
                          <Input type="time" value={editing.end_time} onChange={e => setEditing(p => ({ ...p, end_time: e.target.value }))} className="h-8 text-sm" data-testid={`edit-shift-end-${s.schedule_id}`} />
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(null)} className="h-7 text-[11px] px-2">Cancel</Button>
                        <Button size="sm" onClick={saveEdit} data-testid={`save-edit-shift-${s.schedule_id}`}
                          className="h-7 bg-slate-900 text-white text-[11px] px-3 rounded-full hover:bg-slate-800">
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                  <div key={s.schedule_id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 border"
                    style={{ borderColor: `${locColor(s.location_id)}40`, backgroundColor: `${locColor(s.location_id)}08` }}
                    data-testid={`shift-row-${s.schedule_id}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: locColor(s.location_id) }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-slate-900 truncate tabular-nums">
                          {formatTime12(s.start_time)} – {formatTime12(s.end_time)}
                        </p>
                        <p className="text-[10px] font-bold uppercase tracking-wider font-['Manrope']" style={{ color: locColor(s.location_id) }}>
                          {locName(s.location_id)}
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-400 tabular-nums font-['Manrope']">
                        {((minutesFromHHMM(s.end_time) - minutesFromHHMM(s.start_time)) / 60).toFixed(1)}h
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(s)} className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600" data-testid={`edit-shift-${s.schedule_id}`}>
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(s.schedule_id)} className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" data-testid={`remove-shift-${s.schedule_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  )
                ))}
                {adding === d.idx && (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3 space-y-2" data-testid="add-shift-form">
                    <Select value={form.location_id} onValueChange={v => setForm(f => ({ ...f, location_id: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Location" /></SelectTrigger>
                      <SelectContent>
                        {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Start</label>
                        <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="h-8 text-sm" data-testid="shift-start-input" />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">End</label>
                        <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="h-8 text-sm" data-testid="shift-end-input" />
                      </div>
                    </div>

                    {/* Repeat-on day chips — clicking toggles each extra day in the set.
                        The triggering day (d.idx) is always included and shown locked. */}
                    <div data-testid="repeat-on-row">
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Repeat on</p>
                      <div className="flex flex-wrap gap-1">
                        {DAY_LABELS.map(rd => {
                          const isAnchor = rd.idx === d.idx;
                          const selected = isAnchor || (form.repeat_days || []).includes(rd.idx);
                          return (
                            <button
                              key={rd.idx}
                              type="button"
                              disabled={isAnchor}
                              onClick={() => {
                                if (isAnchor) return;
                                setForm(f => ({
                                  ...f,
                                  repeat_days: selected
                                    ? (f.repeat_days || []).filter(x => x !== rd.idx)
                                    : [...(f.repeat_days || []), rd.idx],
                                }));
                              }}
                              data-testid={`repeat-day-${rd.idx}`}
                              aria-pressed={selected}
                              className={`w-8 h-8 rounded-full text-[11px] font-semibold tabular-nums transition-all font-['Manrope']
                                ${selected
                                  ? "bg-slate-900 text-white"
                                  : "bg-white border border-slate-200 text-slate-500 hover:border-slate-400"}
                                ${isAnchor ? "ring-2 ring-offset-1 ring-indigo-300 cursor-default" : ""}
                              `}
                              title={isAnchor ? "Current day (always included)" : rd.long}
                            >
                              {rd.short[0]}
                            </button>
                          );
                        })}
                        <div className="ml-auto flex items-center gap-1">
                          <button type="button"
                            onClick={() => setForm(f => ({ ...f, repeat_days: [0, 1, 2, 3, 4].filter(x => x !== d.idx) }))}
                            data-testid="preset-weekdays"
                            className="text-[10px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100">
                            Weekdays
                          </button>
                          <button type="button"
                            onClick={() => setForm(f => ({ ...f, repeat_days: [0, 1, 2, 3, 4, 5, 6].filter(x => x !== d.idx) }))}
                            data-testid="preset-everyday"
                            className="text-[10px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100">
                            Every day
                          </button>
                        </div>
                      </div>
                      {(form.repeat_days || []).length > 0 && (
                        <p className="text-[10px] text-indigo-600 mt-1 font-['Manrope']" data-testid="repeat-summary">
                          Will create {1 + (form.repeat_days || []).length} shifts — one per selected day, each repeating every week.
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setAdding(null); setForm(f => ({ ...f, repeat_days: [] })); }} className="h-7 text-[11px] px-2">Cancel</Button>
                      <Button size="sm" onClick={save} data-testid="save-shift-btn"
                        className="h-7 bg-slate-900 text-white text-[11px] px-3 rounded-full hover:bg-slate-800">
                        {((form.repeat_days || []).length) > 0 ? `Add ${1 + form.repeat_days.length} Shifts` : "Add Shift"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccessLevelSelector({ userId, currentLevelId, onUpdate }) {
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/access-levels").then(r => setLevels(r.data)).catch(() => {}); }, []);
  const currentLevel = levels.find(l => l.level_id === currentLevelId) || levels.find(l => l.level_id === "base_employee");
  const assign = async (levelId) => {
    setSaving(true);
    try {
      await api.put(`/staff/${userId}/access-level`, { access_level_id: levelId });
      onUpdate(levelId);
      toast.success(`Access level updated`);
    } catch { toast.error("Failed"); }
    setSaving(false);
  };
  return (
    <div className="flex items-center gap-3" data-testid="access-level-selector">
      <div className="flex-1">
        <Select value={currentLevelId || ""} onValueChange={assign} disabled={saving}>
          <SelectTrigger className="h-10 border-slate-200 rounded-xl text-sm bg-white" data-testid="access-level-select">
            <SelectValue placeholder={currentLevel?.name || "Not assigned"} />
          </SelectTrigger>
          <SelectContent>
            {levels.map(l => (
              <SelectItem key={l.level_id} value={l.level_id}>
                <div className="flex items-center gap-2">
                  <Shield className="w-3 h-3 text-violet-500" />
                  <span>{l.name}</span>
                  {l.is_system && <span className="text-[9px] text-violet-400 ml-1">(system)</span>}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {currentLevel && (
        <p className="text-[10px] text-slate-400 max-w-[150px] font-['Manrope']">{currentLevel.description}</p>
      )}
    </div>
  );
}

const roleColors = {
  stylist: "#8B5CF6", esthetician: "#F59E0B", nail_tech: "#EF4444",
  colorist: "#EC4899", support_staff: "#6B7280", front_desk: "#6B7280", owner: "#1A1A1A", manager: "#3B82F6",
};
const empTypeLabel = { "full-time": "Full-Time", "part-time": "Part-Time", "contract": "Contract" };

export default function StaffPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [qualifications, setQualifications] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [addingService, setAddingService] = useState(false);
  const [bulkServicesOpen, setBulkServicesOpen] = useState(false);
  const [newQual, setNewQual] = useState({ service_id: "", custom_price: "", custom_duration_minutes: "" });
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState("deactivate"); // "deactivate" | "delete"
  const [deletePin, setDeletePin] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  // Owner-promotion PIN gate: if the role is being changed to "owner" we
  // open a small dialog asking for the manager PIN before submitting.
  const [ownerPromotePin, setOwnerPromotePin] = useState("");
  const [ownerPromoteOpen, setOwnerPromoteOpen] = useState(false);
  const [ownerPromoteError, setOwnerPromoteError] = useState("");
  const [profileTab, setProfileTab] = useState("details");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMode, setResetMode] = useState("manual");
  // Emergency-owner-reset acknowledgement: when a current-user-owner is
  // resetting *another* owner's password we show a red banner + require an
  // explicit checkbox so it's clearly an intentional emergency action.
  const [ownerResetAck, setOwnerResetAck] = useState(false);
  // Tracks the most recent successful manual reset so we can show a clear
  // post-success confirmation card with the password the owner just set.
  // (Cleared when the dialog closes.)
  const [manualResetSuccess, setManualResetSuccess] = useState(null);
  const [showResetPwd, setShowResetPwd] = useState(false);
  // Revoke-sessions dialog state
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeSaving, setRevokeSaving] = useState(false);
  // Bulk selection & delete
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkPin, setBulkPin] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [resetPwd, setResetPwd] = useState("");
  const [resetPwd2, setResetPwd2] = useState("");
  const [resetSaving, setResetSaving] = useState(false); // "details" | "schedule" | "services"
  // Employee PIN management (inside Edit)
  const [pinInput, setPinInput] = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: "", email: "", phone: "", role: "stylist", title: "",
    location_ids: [], commission_rate: 0, hourly_rate: 0, color: "#B5A493",
    birthday: "", bio: "", employment_type: "full-time", start_date: "",
    initial_password: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [staffRes, locRes] = await Promise.all([
          api.get(`/staff/all${includeInactive ? "?include_inactive=true" : ""}`),
          api.get("/locations"),
        ]);
        setStaff(staffRes.data);
        setLocations(locRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [includeInactive]);

  const handleCreate = async () => {
    if (!newStaff.name?.trim() || !newStaff.email?.trim()) {
      toast.error("Name and email are required"); return;
    }
    if (newStaff.initial_password && newStaff.initial_password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    try {
      const payload = { ...newStaff };
      if (!payload.initial_password) delete payload.initial_password;
      await api.post("/staff", payload);
      const res = await api.get("/staff/all");
      setStaff(res.data);
      setShowNew(false);
      setNewStaff({ name: "", email: "", phone: "", role: "stylist", title: "", location_ids: [], commission_rate: 0, hourly_rate: 0, color: "#B5A493", birthday: "", bio: "", employment_type: "full-time", start_date: "", initial_password: "" });
      toast.success(payload.initial_password ? "Team member added — they can log in now" : "Team member added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add team member");
    }
  };

  const openProfile = async (userId) => {
    try {
      const [profRes, qualRes, svcRes] = await Promise.all([
        api.get(`/staff/${userId}/profile`),
        api.get(`/staff/${userId}/qualifications`),
        api.get("/services"),
      ]);
      setProfile(profRes.data);
      setQualifications(qualRes.data || []);
      setAllServices(svcRes.data || []);
      setEditing(false);
      setAddingService(false);
      setProfileTab("details");
    } catch { toast.error("Failed to load profile"); }
  };

  const saveQualPrice = async (qual, newPrice) => {
    try {
      await api.put(`/staff/${profile.user_id}/qualifications`, {
        service_id: qual.service_id, custom_price: parseFloat(newPrice), qualified: true,
        // Preserve existing custom duration when only the price changes
        custom_duration_minutes: qual.custom_duration_minutes ?? null,
      });
      toast.success("Price updated");
      const res = await api.get(`/staff/${profile.user_id}/qualifications`);
      setQualifications(res.data || []);
    } catch { toast.error("Failed to save"); }
  };

  const saveQualDuration = async (qual, newDuration) => {
    try {
      const dur = newDuration === "" || newDuration === null ? null : parseInt(newDuration);
      await api.put(`/staff/${profile.user_id}/qualifications`, {
        service_id: qual.service_id, custom_price: qual.custom_price, qualified: true,
        custom_duration_minutes: dur,
      });
      toast.success("Duration updated");
      const res = await api.get(`/staff/${profile.user_id}/qualifications`);
      setQualifications(res.data || []);
    } catch { toast.error("Failed to save"); }
  };

  // Bulk selection handlers
  const toggleSelect = (uid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const selectAllDeletable = (list) => {
    // Owners can be selected for bulk-delete only when the current user is
    // also an owner (managers can't touch owners). Self can never be
    // selected — that's a guardrail at the API layer too.
    const isOwner = currentUser?.role === "owner";
    const ids = list
      .filter(s => s.user_id !== currentUser?.user_id)
      .filter(s => isOwner || s.role !== "owner")
      .map(s => s.user_id);
    setSelectedIds(new Set(ids));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const cancelSelectMode = () => { setSelectMode(false); clearSelection(); };

  const confirmBulkDelete = async () => {
    if (!bulkPin || bulkPin.length < 4) { toast.error("Enter your manager PIN"); return; }
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await api.post("/staff/bulk-delete",
        { user_ids: Array.from(selectedIds) },
        { headers: { "X-Manager-Pin": bulkPin } }
      );
      const { deleted_count = 0, skipped = [] } = res.data || {};
      if (deleted_count > 0) toast.success(`${deleted_count} team member${deleted_count === 1 ? "" : "s"} removed`);
      if (skipped.length) {
        const reasons = skipped.map(s => `${s.name || s.user_id}: ${s.reason}`).join("; ");
        toast.info(`Skipped ${skipped.length}: ${reasons}`);
      }
      setBulkDeleteOpen(false);
      setBulkPin("");
      clearSelection();
      setSelectMode(false);
      const refreshed = await api.get("/staff/all");
      setStaff(refreshed.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
    setBulkDeleting(false);
  };

  const revokeSessions = async () => {
    if (!profile) return;
    setRevokeSaving(true);
    try {
      await api.post(`/staff/${profile.user_id}/revoke-sessions`);
      const isSelf = profile.user_id === currentUser?.user_id;
      toast.success(isSelf
        ? "Signed out everywhere else. You're still signed in here."
        : `${profile.name} has been signed out on every device.`);
      setRevokeOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke sessions");
    } finally {
      setRevokeSaving(false);
    }
  };


  const resetStaffPassword = async () => {
    if (!resetPwd || resetPwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (resetPwd !== resetPwd2) { toast.error("Passwords don't match"); return; }
    setResetSaving(true);
    try {
      await api.put(`/staff/${profile.user_id}/password`, { new_password: resetPwd });
      toast.success(`Password reset for ${profile.name}`);
      // Don't close the dialog — show a confirmation card with the password
      // the owner just set, so they can copy/share it and we eliminate any
      // doubt that "the password didn't take".
      setManualResetSuccess({
        password: resetPwd,
        email: profile.email,
        name: profile.name,
        savedAt: new Date().toISOString(),
      });
      setResetPwd("");
      setResetPwd2("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    }
    setResetSaving(false);
  };

  const [emailLinkResult, setEmailLinkResult] = useState(null); // {reset_url, email_sent, ...}
  const sendResetLink = async () => {
    setResetSaving(true);
    setEmailLinkResult(null);
    try {
      const res = await api.post(`/staff/${profile.user_id}/send-password-reset`);
      setEmailLinkResult(res.data);
      if (res.data?.email_sent) {
        toast.success(`Reset link emailed to ${profile.email}`);
      } else {
        toast.info("Email not configured — share the link below manually");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reset link");
    }
    setResetSaving(false);
  };

  // Employee PIN (inside Edit form)
  const saveEmployeePin = async () => {
    if (!/^\d{4}$/.test(pinInput)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    setPinSaving(true);
    try {
      await api.post("/employee-pins/set", { user_id: profile.user_id, pin: pinInput });
      toast.success(`PIN ${profile.has_pin ? "updated" : "created"} for ${profile.name}`);
      setPinInput("");
      setProfile(p => p ? { ...p, has_pin: true } : p);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save PIN");
    }
    setPinSaving(false);
  };

  const clearEmployeePin = async () => {
    if (!window.confirm(`Remove ${profile.name}'s kiosk PIN? They'll need a new one to clock in.`)) return;
    setPinSaving(true);
    try {
      await api.delete(`/employee-pins/${profile.user_id}`);
      toast.success(`PIN cleared for ${profile.name}`);
      setPinInput("");
      setProfile(p => p ? { ...p, has_pin: false } : p);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear PIN");
    }
    setPinSaving(false);
  };

  const addQualification = async () => {
    if (!newQual.service_id) return;
    const svc = allServices.find(s => s.service_id === newQual.service_id);
    try {
      await api.put(`/staff/${profile.user_id}/qualifications`, {
        service_id: newQual.service_id,
        custom_price: newQual.custom_price ? parseFloat(newQual.custom_price) : (svc?.price || 0),
        custom_duration_minutes: newQual.custom_duration_minutes ? parseInt(newQual.custom_duration_minutes) : null,
        qualified: true, source: "manual",
      });
      toast.success("Service added");
      setAddingService(false);
      setNewQual({ service_id: "", custom_price: "", custom_duration_minutes: "" });
      const res = await api.get(`/staff/${profile.user_id}/qualifications`);
      setQualifications(res.data || []);
    } catch { toast.error("Failed"); }
  };

  const removeQualification = async (serviceId) => {
    try {
      await api.delete(`/staff/${profile.user_id}/qualifications/${serviceId}`);
      toast.success("Service removed");
      const res = await api.get(`/staff/${profile.user_id}/qualifications`);
      setQualifications(res.data || []);
    } catch { toast.error("Failed"); }
  };

  const startEdit = () => {
    setEditForm({
      name: profile.name || "", email: profile.email || "", phone: profile.phone || "",
      title: profile.title || "", role: profile.role || "stylist",
      birthday: profile.birthday || "", bio: profile.bio || "",
      employment_type: profile.employment_type || "full-time",
      start_date: profile.start_date || "",
      emergency_contact: profile.emergency_contact || "",
      emergency_phone: profile.emergency_phone || "",
      pay_type: profile.pay_type || (profile.commission_rate > 0 ? "commission" : profile.hourly_rate > 0 ? "hourly" : "commission"),
      commission_rate: profile.commission_rate || 0,
      hourly_rate: profile.hourly_rate || 0,
      color: profile.color || "#B5A493",
      specializations: (profile.specializations || []).join(", "),
      is_educator: !!profile.is_educator,
    });
    setEditing(true);
    setPinInput("");
  };

  const confirmDelete = async () => {
    if (deletePin.length < 4) {
      setDeleteError("Enter the manager PIN to confirm");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      if (deleteMode === "deactivate") {
        const res = await api.post(`/staff/${profile.user_id}/deactivate`, {}, {
          headers: { "X-Manager-Pin": deletePin },
        });
        const u = res.data?.future_appointments_unassigned || 0;
        toast.success(`${profile.name} deactivated${u ? ` — ${u} future appointment${u > 1 ? "s" : ""} unassigned` : ""}`);
      } else {
        const res = await api.delete(`/staff/${profile.user_id}`, {
          headers: { "X-Manager-Pin": deletePin },
        });
        const c = res.data?.cascade || {};
        const removed = Object.values(c).reduce((a, b) => a + b, 0);
        toast.success(`${profile.name} deleted${removed ? ` — cleaned up ${removed} related records` : ""}`);
      }
      setDeletePromptOpen(false);
      setDeletePin("");
      setDeleteMode("deactivate");
      setProfile(null);
      const r = await api.get(`/staff/all${includeInactive ? "?include_inactive=true" : ""}`);
      setStaff(r.data);
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Failed");
    }
    setDeleting(false);
  };

  const reactivate = async () => {
    try {
      await api.post(`/staff/${profile.user_id}/reactivate`);
      toast.success(`${profile.name} reactivated`);
      setProfile(null);
      const r = await api.get(`/staff/all${includeInactive ? "?include_inactive=true" : ""}`);
      setStaff(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reactivate");
    }
  };

  const saveEdit = async (managerPin = null) => {
    // If promoting a non-owner to owner, require the manager PIN. We open
    // the prompt dialog and bail out — saveEdit gets re-invoked with the
    // PIN once the user confirms.
    const isPromotingToOwner = editForm.role === "owner" && profile?.role !== "owner";
    if (isPromotingToOwner && !managerPin) {
      setOwnerPromotePin("");
      setOwnerPromoteError("");
      setOwnerPromoteOpen(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        specializations: editForm.specializations ? editForm.specializations.split(",").map(s => s.trim()).filter(Boolean) : [],
        pay_type: editForm.pay_type || "commission",
        commission_rate: editForm.pay_type === "hourly" ? 0 : (parseFloat(editForm.commission_rate) || 0),
        hourly_rate: editForm.pay_type === "commission" ? (parseFloat(editForm.hourly_rate) || 0) : (parseFloat(editForm.hourly_rate) || 0),
      };
      const config = managerPin ? { headers: { "X-Manager-Pin": managerPin } } : {};
      await api.put(`/staff/${profile.user_id}`, payload, config);
      toast.success(isPromotingToOwner ? `${profile.name} promoted to Owner` : "Profile updated");
      setOwnerPromoteOpen(false);
      setOwnerPromotePin("");
      openProfile(profile.user_id);
      const res = await api.get("/staff/all");
      setStaff(res.data);
    } catch (err) {
      // Surface the actual server error so owners can self-diagnose instead
      // of staring at a generic "Failed to save". Covers: 400 validation
      // (e.g. bad role), 403 role-change not allowed, 404 target missing,
      // network outage, etc. The breadcrumb in DevTools captures the full
      // error for bug reports.
      // eslint-disable-next-line no-console
      console.error("Save profile failed", { status: err.response?.status, data: err.response?.data, message: err.message });
      const detail = typeof err.response?.data?.detail === "string" ? err.response.data.detail : null;
      let msg = detail;
      if (!msg) {
        if (err.response?.status === 403) msg = "You don't have permission to change this profile.";
        else if (err.response?.status === 404) msg = "Couldn't find this staff member — try reloading the page.";
        else if (err.response?.status >= 500) msg = `Server error (${err.response.status}). Try again in a moment.`;
        else if (err.response?.status) msg = `Save failed (HTTP ${err.response.status}).`;
        else msg = "Couldn't reach the server. Check your internet and try again.";
      }
      toast.error(msg);
      // If we were inside the promotion flow and the failure was the manager
      // PIN, surface the error inline in the dialog so the user can retry.
      if (managerPin && err.response?.status === 403 && /pin/i.test(detail || "")) {
        setOwnerPromoteError(detail || "Incorrect manager PIN");
      }
    }
    setSaving(false);
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };

  const tenure = (startDate) => {
    if (!startDate) return "";
    try {
      const s = new Date(startDate);
      const diff = Date.now() - s.getTime();
      const years = Math.floor(diff / (365.25 * 86400000));
      const months = Math.floor((diff % (365.25 * 86400000)) / (30.44 * 86400000));
      return years > 0 ? `${years}y ${months}m` : `${months}m`;
    } catch { return ""; }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="staff-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900">Team</h1>
          <p className="text-slate-600 text-sm mt-1">{staff.length} team members</p>
        </div>
        <div className="flex items-center gap-2">
          {selectMode ? (
            <>
              <span className="text-xs font-semibold text-slate-600 font-['Manrope']" data-testid="selection-count">
                {selectedIds.size} selected
              </span>
              <Button variant="outline" size="sm" onClick={() => selectAllDeletable(staff)}
                data-testid="select-all-btn"
                className="h-9 rounded-full text-xs">Select all</Button>
              <Button variant="outline" size="sm" onClick={clearSelection}
                className="h-9 rounded-full text-xs">Clear</Button>
              <Button onClick={() => setBulkDeleteOpen(true)} disabled={selectedIds.size === 0}
                data-testid="bulk-delete-btn"
                className="h-9 rounded-full bg-rose-600 text-white hover:bg-rose-700 text-xs disabled:opacity-40 disabled:pointer-events-none">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete {selectedIds.size ? `(${selectedIds.size})` : ""}
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelSelectMode}
                data-testid="exit-select-mode"
                className="h-9 rounded-full text-xs">
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
            </>
          ) : (
            <>
              <label className="inline-flex items-center gap-2 px-3 h-10 rounded-full border border-slate-200 bg-white cursor-pointer hover:border-slate-300 transition-colors" data-testid="include-inactive-toggle">
                <input type="checkbox" checked={includeInactive}
                  onChange={e => setIncludeInactive(e.target.checked)}
                  className="rounded border-slate-300 text-slate-900 focus:ring-slate-400 w-3.5 h-3.5" />
                <span className="text-xs font-medium text-slate-700 font-['Manrope']">Show deactivated</span>
              </label>
              {currentUser?.role === "owner" || currentUser?.role === "manager" ? (
                <Button variant="outline" onClick={() => setSelectMode(true)}
                  data-testid="enter-select-mode"
                  className="h-10 rounded-full text-xs border-slate-200">
                  <Check className="w-3.5 h-3.5 mr-1.5" /> Select
                </Button>
              ) : null}
              <Button data-testid="new-staff-btn" onClick={() => setShowNew(true)} className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm">
                <Plus className="w-4 h-4 mr-1.5" /> Add Team Member
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Staff Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((m) => {
          // Owners can be bulk-selected for deletion only when the current
          // user is an owner — managers can't delete other owners. Self is
          // always blocked (you can't delete your own account).
          const canTargetThisUser = m.user_id !== currentUser?.user_id
            && (currentUser?.role === "owner" || m.role !== "owner");
          const isSelectable = selectMode && canTargetThisUser;
          const isSelected = selectedIds.has(m.user_id);
          return (
          <div key={m.user_id} data-testid={`staff-card-${m.user_id}`}
            className={`relative bg-white border rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer ${
              isSelected ? "border-rose-400 ring-2 ring-rose-200 shadow-sm" :
              selectMode && !isSelectable ? "border-slate-100 opacity-50 cursor-not-allowed" :
              "border-slate-200"
            } ${m.is_active === false ? "opacity-60 border-dashed" : ""}`}
            onClick={(e) => {
              if (selectMode) {
                if (!isSelectable) return;
                toggleSelect(m.user_id);
              } else {
                openProfile(m.user_id);
              }
            }}>
            {/* Selection checkbox */}
            {selectMode && (
              <div className="absolute top-3 left-3 z-10">
                {isSelectable ? (
                  <div data-testid={`select-checkbox-${m.user_id}`}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? "bg-rose-500 border-rose-500" : "bg-white border-slate-300"}`}>
                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-md border-2 border-slate-200 bg-slate-100 flex items-center justify-center"
                    title={
                      m.user_id === currentUser?.user_id
                        ? "You can't delete yourself"
                        : (m.role === "owner" && currentUser?.role !== "owner")
                          ? "Only an owner can delete another owner"
                          : "Locked"
                    }>
                    <Shield className="w-3 h-3 text-slate-300" />
                  </div>
                )}
              </div>
            )}
            <div className={`flex items-start gap-4 ${selectMode ? "pl-6" : ""}`}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-medium flex-shrink-0"
                style={{ backgroundColor: m.color || roleColors[m.role] || '#B5A493' }}>
                {m.name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-900 truncate">{m.name}</h3>
                  {m.is_active === false && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-100">Deactivated</span>}
                </div>
                <p className="text-xs text-slate-600">{m.title || m.role?.replace('_', ' ')}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium capitalize"
                    style={{ backgroundColor: `${roleColors[m.role] || '#B5A493'}15`, color: roleColors[m.role] || '#B5A493' }}>
                    {m.role?.replace('_', ' ')}
                  </span>
                  {m.employment_type && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAFAFA] text-slate-400 border border-slate-200">
                      {empTypeLabel[m.employment_type] || m.employment_type}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#D1D1D1] flex-shrink-0 mt-1" />
            </div>

            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
              {m.email && <span className="flex items-center gap-1.5 text-slate-600 truncate"><Mail className="w-3 h-3 text-rose-400" />{m.email}</span>}
              {m.phone && <span className="flex items-center gap-1.5 text-slate-600"><Phone className="w-3 h-3 text-rose-400" />{m.phone}</span>}
              {m.start_date && <span className="flex items-center gap-1.5 text-slate-600"><Calendar className="w-3 h-3 text-rose-400" />{tenure(m.start_date)}</span>}
              {m.birthday && <span className="flex items-center gap-1.5 text-slate-600"><Cake className="w-3 h-3 text-rose-400" />{fmtDate(m.birthday)}</span>}
            </div>

            {(m.specializations || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {m.specializations.slice(0, 3).map(s => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#FAFAFA] text-slate-400 border border-slate-200">{s}</span>
                ))}
                {m.specializations.length > 3 && <span className="text-[10px] text-rose-400">+{m.specializations.length - 3}</span>}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Profile Slide-out */}
      {profile && (
        <div className="fixed inset-0 bg-black/30 flex items-stretch justify-end z-50" onClick={() => setProfile(null)}>
          <div data-testid="staff-profile-panel" className="bg-white w-full max-w-lg overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            {/* Profile Header */}
            <div className="p-6 border-b border-slate-200" style={{ background: `linear-gradient(135deg, ${profile.color || '#1A1A1A'}15, transparent)` }}>
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="sm" onClick={() => setProfile(null)}><X className="w-4 h-4" /></Button>
                <div className="flex items-center gap-1.5">
                  {(currentUser?.role === "owner" || currentUser?.role === "manager") && (
                    <Button variant="outline" size="sm" onClick={() => { setResetOpen(true); setResetPwd(""); setResetPwd2(""); }}
                      data-testid="reset-password-btn"
                      className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300">
                      <KeyRound className="w-3 h-3 mr-1" /> Reset Password
                    </Button>
                  )}
                  {currentUser?.role === "owner" && profile.is_active !== false && (
                    <Button variant="outline" size="sm" onClick={() => setRevokeOpen(true)}
                      data-testid="revoke-sessions-btn"
                      title={profile.user_id === currentUser?.user_id ? "Sign yourself out on every other device" : `Sign ${profile.name?.split(" ")[0] || "this user"} out on every device`}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300">
                      <LogOut className="w-3 h-3 mr-1" /> Log out everywhere
                    </Button>
                  )}
                  {profile.is_active === false && profile.user_id !== currentUser?.user_id && (
                    <Button variant="outline" size="sm" onClick={reactivate}
                      data-testid="reactivate-staff-btn"
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300">
                      <RefreshCw className="w-3 h-3 mr-1" /> Reactivate
                    </Button>
                  )}
                  {profile.is_active !== false && profile.user_id !== currentUser?.user_id && (currentUser?.role === "owner" || profile.role !== "owner") && (
                    <Button variant="outline" size="sm" onClick={() => { setDeleteMode("deactivate"); setDeletePromptOpen(true); }}
                      data-testid="delete-staff-btn"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300">
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={startEdit} className="border-slate-200" data-testid="edit-profile-btn"><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                  <Button size="sm" onClick={() => navigate(`/staff/${profile.user_id}`)} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="open-full-page-btn"><ChevronRight className="w-3 h-3 mr-1" /> Full page</Button>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-semibold"
                  style={{ backgroundColor: profile.color || '#1A1A1A' }}>
                  {profile.name?.[0]}
                </div>
                <div>
                  <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">{profile.name}</h2>
                  <p className="text-sm text-slate-600">{profile.title || profile.role?.replace('_', ' ')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full capitalize font-medium"
                      style={{ backgroundColor: `${roleColors[profile.role] || '#B5A493'}15`, color: roleColors[profile.role] || '#B5A493' }}>
                      {profile.role?.replace('_', ' ')}
                    </span>
                    {profile.employment_type && <span className="text-xs text-slate-400">{empTypeLabel[profile.employment_type]}</span>}
                    {profile.clocked_in && <span className="text-xs bg-[#D4EDDA] text-[#155724] px-2 py-0.5 rounded-full">Clocked In</span>}
                  </div>
                </div>
              </div>
            </div>

            {editing ? (
              /* Edit Form */
              <div className="p-6 space-y-4" data-testid="edit-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 mb-1 block">Name</label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="border-slate-200 h-9 text-sm" data-testid="edit-name" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Title</label><Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="border-slate-200 h-9 text-sm" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Email</label><Input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="border-slate-200 h-9 text-sm" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Phone</label><Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="border-slate-200 h-9 text-sm" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Birthday</label><Input type="date" value={editForm.birthday} onChange={e => setEditForm(f => ({ ...f, birthday: e.target.value }))} className="border-slate-200 h-9 text-sm" data-testid="edit-birthday" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Start Date</label><Input type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} className="border-slate-200 h-9 text-sm" data-testid="edit-start-date" /></div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Role</label>
                    <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                      <SelectTrigger className="h-9 border-slate-200 text-sm" data-testid="edit-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stylist">Stylist</SelectItem>
                        <SelectItem value="colorist">Colorist</SelectItem>
                        <SelectItem value="esthetician">Esthetician</SelectItem>
                        <SelectItem value="nail_tech">Nail Tech</SelectItem>
                        <SelectItem value="educator">Educator</SelectItem>
                        <SelectItem value="front_desk">Front Desk</SelectItem>
                        <SelectItem value="support_staff">Support Staff</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        {/* Owners (current user) can promote any non-owner to owner. The
                            backend additionally requires the manager PIN — captured via the
                            dialog below. Managers and lower roles cannot select this option. */}
                        <SelectItem value="owner" disabled={currentUser?.role !== "owner"}>Owner</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Employment</label>
                    <Select value={editForm.employment_type} onValueChange={v => setEditForm(f => ({ ...f, employment_type: v }))}>
                      <SelectTrigger className="h-9 border-slate-200 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full-time">Full-Time</SelectItem>
                        <SelectItem value="part-time">Part-Time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Color</label><Input type="color" value={editForm.color} onChange={e => setEditForm(f => ({ ...f, color: e.target.value }))} className="border-slate-200 h-9 p-1" /></div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Pay Type</label>
                    <Select value={editForm.pay_type || "commission"} onValueChange={v => setEditForm(f => ({ ...f, pay_type: v }))}>
                      <SelectTrigger className="border-slate-200 h-9 text-sm" data-testid="edit-pay-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="commission">Commission</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editForm.pay_type === "commission" ? (
                    <div><label className="text-xs text-slate-400 mb-1 block">Commission %</label>
                      <Input type="number" step="0.01" min="0" max="1" value={editForm.commission_rate}
                        onChange={e => setEditForm(f => ({ ...f, commission_rate: e.target.value }))}
                        className="border-slate-200 h-9 text-sm" data-testid="edit-commission-rate"
                        placeholder="0.40 = 40%" />
                    </div>
                  ) : (
                    <div><label className="text-xs text-slate-400 mb-1 block">Hourly Rate ($/hr)</label>
                      <Input type="number" step="0.25" min="0" value={editForm.hourly_rate}
                        onChange={e => setEditForm(f => ({ ...f, hourly_rate: e.target.value }))}
                        className="border-slate-200 h-9 text-sm" data-testid="edit-hourly-rate"
                        placeholder="20.00" />
                    </div>
                  )}
                </div>
                <div><label className="text-xs text-slate-400 mb-1 block">Bio</label><textarea value={editForm.bio} onChange={e => setEditForm(f => ({ ...f, bio: e.target.value }))} className="w-full border border-slate-200 rounded-lg p-2 text-sm h-20 resize-none" data-testid="edit-bio" /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Specializations (comma-separated)</label><Input value={editForm.specializations} onChange={e => setEditForm(f => ({ ...f, specializations: e.target.value }))} className="border-slate-200 h-9 text-sm" /></div>

                {/* Permissions */}
                <div className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-4" data-testid="edit-permissions-section">
                  <h4 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-500" /> Permissions
                  </h4>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editForm.is_educator || editForm.role === "educator"}
                      onChange={e => setEditForm(f => ({ ...f, is_educator: e.target.checked }))}
                      data-testid="edit-is-educator"
                      disabled={editForm.role === "educator"}
                      className="mt-0.5 rounded border-slate-300 text-rose-500 focus:ring-rose-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Can teach training sessions</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                        Adds {profile?.name?.split(" ")[0] || "this team member"} to the educator picker on every training session and evaluation. Use this for senior stylists who teach without changing their primary role. {editForm.role === "educator" && <span className="text-rose-600 font-medium">Always on for the Educator role.</span>}
                      </p>
                    </div>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 mb-1 block">Emergency Contact</label><Input value={editForm.emergency_contact} onChange={e => setEditForm(f => ({ ...f, emergency_contact: e.target.value }))} className="border-slate-200 h-9 text-sm" /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Emergency Phone</label><Input value={editForm.emergency_phone} onChange={e => setEditForm(f => ({ ...f, emergency_phone: e.target.value }))} className="border-slate-200 h-9 text-sm" /></div>
                </div>

                {/* Employee Kiosk PIN — owner/manager only */}
                {(currentUser?.role === "owner" || currentUser?.role === "manager") && profile.user_id !== currentUser?.user_id && (
                  <div className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-4" data-testid="employee-pin-section">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-slate-500" />
                        <h4 className="text-sm font-medium text-slate-900">Kiosk &amp; Portal PIN</h4>
                      </div>
                      {profile.has_pin ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8F0E9] text-emerald-700 font-medium flex items-center gap-1">
                          <Check className="w-3 h-3" /> Set
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF1F2] text-rose-700 font-medium">
                          Not set
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
                      4-digit PIN used for kiosk clock-in and the Employee Portal quick login. {profile.name?.split(" ")[0]} can also change it from their own portal.
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="edit-employee-pin-input"
                        value={pinInput}
                        onChange={e => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        type="password"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={4}
                        placeholder="• • • •"
                        className="border-slate-200 h-9 text-sm tracking-[0.4em] text-center max-w-[140px]"
                      />
                      <Button
                        size="sm"
                        onClick={saveEmployeePin}
                        disabled={pinSaving || pinInput.length !== 4}
                        data-testid="edit-employee-pin-save"
                        className="bg-slate-900 text-white hover:bg-slate-800 h-9">
                        {pinSaving ? "Saving…" : profile.has_pin ? "Update PIN" : "Set PIN"}
                      </Button>
                      {profile.has_pin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={clearEmployeePin}
                          disabled={pinSaving}
                          data-testid="edit-employee-pin-clear"
                          className="border-rose-200 text-rose-600 hover:bg-rose-50 h-9">
                          <Trash2 className="w-3 h-3 mr-1" /> Clear
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button onClick={() => saveEdit()} disabled={saving} className="bg-slate-900 text-white" data-testid="save-profile-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}</Button>
                  <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-200">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="flex items-center gap-1 px-5 pt-4 border-b border-slate-100" data-testid="profile-tabs">
                  {[
                    { id: "details", label: "Details", icon: UserCog },
                    { id: "schedule", label: "Schedule", icon: CalendarClock },
                  ].map(t => (
                    <button key={t.id}
                      data-testid={`tab-${t.id}`}
                      onClick={() => setProfileTab(t.id)}
                      className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px ${profileTab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                      <t.icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  ))}
                </div>

                {profileTab === "schedule" ? (
                  <div className="p-6">
                    <ScheduleTab userId={profile.user_id} locations={locations} />
                  </div>
                ) : (
              /* Profile View */
              <div className="p-6 space-y-5">
                {/* Bio */}
                {profile.bio && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Bio</p>
                    <p className="text-sm text-[#3D3D3D] leading-relaxed">{profile.bio}</p>
                  </div>
                )}

                {/* Contact Info */}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Contact</p>
                  <div className="space-y-2">
                    {profile.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-4 h-4 text-rose-400" /> {profile.email}</div>}
                    {profile.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-4 h-4 text-rose-400" /> {profile.phone}</div>}
                    {profile.birthday && <div className="flex items-center gap-2 text-sm text-slate-600"><Cake className="w-4 h-4 text-rose-400" /> {fmtDate(profile.birthday)}</div>}
                    {profile.location_ids?.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-rose-400" /> {profile.location_ids.map(lid => locations.find(l => l.location_id === lid)?.name || lid).join(", ")}</div>
                    )}
                  </div>
                </div>

                {/* Employment Details */}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Employment</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#FAFAFA] rounded-lg p-3">
                      <Briefcase className="w-4 h-4 text-rose-400 mb-1" />
                      <p className="text-sm font-medium text-slate-900">{empTypeLabel[profile.employment_type] || "—"}</p>
                      <p className="text-[10px] text-slate-400">Employment Type</p>
                    </div>
                    <div className="bg-[#FAFAFA] rounded-lg p-3">
                      <Calendar className="w-4 h-4 text-rose-400 mb-1" />
                      <p className="text-sm font-medium text-slate-900">{fmtDate(profile.start_date)}</p>
                      <p className="text-[10px] text-slate-400">Start Date{profile.stats?.tenure_years > 0 ? ` (${profile.stats.tenure_years}y)` : ""}</p>
                    </div>
                    {profile.pay_type === "hourly" ? (
                      <div className="bg-[#FAFAFA] rounded-lg p-3" data-testid="profile-pay-hourly">
                        <Clock className="w-4 h-4 text-rose-400 mb-1" />
                        <p className="text-sm font-medium text-slate-900">${Number(profile.hourly_rate || 0).toFixed(2)}/hr</p>
                        <p className="text-[10px] text-slate-400">Hourly Pay</p>
                      </div>
                    ) : (
                      <div className="bg-[#FAFAFA] rounded-lg p-3" data-testid="profile-pay-commission">
                        <DollarSign className="w-4 h-4 text-rose-400 mb-1" />
                        <p className="text-sm font-medium text-slate-900">{((profile.commission_rate || 0) * 100).toFixed(0)}%</p>
                        <p className="text-[10px] text-slate-400">Commission</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Access Level */}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Access Level</p>
                  <AccessLevelSelector userId={profile.user_id} currentLevelId={profile.access_level_id} onUpdate={(lvlId) => { setProfile(p => ({...p, access_level_id: lvlId})); }} />
                </div>

                {/* Performance Stats */}
                {profile.stats && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Performance</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center bg-[#FAFAFA] rounded-lg p-3">
                        <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{profile.stats.month_appointments}</p>
                        <p className="text-[10px] text-slate-400">This Month</p>
                      </div>
                      <div className="text-center bg-[#FAFAFA] rounded-lg p-3">
                        <p className="font-['Outfit'] text-lg font-semibold text-emerald-600">${profile.stats.month_revenue}</p>
                        <p className="text-[10px] text-slate-400">Month Rev</p>
                      </div>
                      <div className="text-center bg-[#FAFAFA] rounded-lg p-3">
                        <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{profile.stats.total_completed}</p>
                        <p className="text-[10px] text-slate-400">All-Time Appts</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Specializations */}
                {(profile.specializations || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Specializations</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.specializations.map(s => (
                        <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#FAFAFA] text-slate-600 border border-slate-200">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Qualified Services & Pricing */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Qualified Services & Pricing</p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setBulkServicesOpen(true)} className="border-slate-200 text-slate-600 text-xs h-7 hover:border-rose-400 hover:text-rose-600 hover:bg-rose-50" data-testid="bulk-add-services-btn">
                        <Plus className="w-3 h-3 mr-1" /> Bulk Add
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAddingService(!addingService)} className="border-slate-200 text-slate-600 text-xs h-7" data-testid="add-service-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add One
                      </Button>
                    </div>
                  </div>
                  {addingService && (
                    <div data-testid="add-qual-form" className="bg-[#FAFAFA] rounded-lg p-3 mb-3 space-y-2">
                      <Select value={newQual.service_id} onValueChange={v => {
                        const svc = allServices.find(s => s.service_id === v);
                        setNewQual({
                          service_id: v,
                          custom_price: svc?.price?.toString() || "",
                          custom_duration_minutes: svc?.duration?.toString() || "",
                        });
                      }}>
                        <SelectTrigger className="h-8 border-slate-200 text-xs bg-white"><SelectValue placeholder="Select service" /></SelectTrigger>
                        <SelectContent>
                          {allServices.filter(s => !qualifications.some(q => q.service_id === s.service_id)).map(s => (
                            <SelectItem key={s.service_id} value={s.service_id}>{s.name} (base ${s.price} · {s.duration}min)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                          <Input type="number" value={newQual.custom_price} onChange={e => setNewQual(q => ({ ...q, custom_price: e.target.value }))} className="border-slate-200 h-8 text-xs pl-5" placeholder="Custom price" data-testid="new-qual-price" />
                        </div>
                        <div className="relative flex-1">
                          <Input type="number" value={newQual.custom_duration_minutes} onChange={e => setNewQual(q => ({ ...q, custom_duration_minutes: e.target.value }))} className="border-slate-200 h-8 text-xs pr-9" placeholder="Min" data-testid="new-qual-duration" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">min</span>
                        </div>
                        <Button size="sm" onClick={addQualification} className="bg-slate-900 text-white h-8 text-xs" data-testid="save-new-qual-btn">Add</Button>
                      </div>
                    </div>
                  )}
                  {qualifications.length === 0 ? (
                    <p className="text-xs text-slate-400 bg-[#FAFAFA] rounded-lg p-3 text-center">No services assigned yet</p>
                  ) : (
                    <div className="space-y-1.5">
                      {qualifications.filter(q => q.qualified).map(q => {
                        const baseDur = q.base_duration_minutes ?? null;
                        const effDur = q.custom_duration_minutes ?? baseDur;
                        return (
                        <div key={q.service_id} data-testid={`qual-${q.service_id}`} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white group">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{q.service_name}</p>
                            <p className="text-[10px] text-slate-400">
                              Base: ${q.base_price}{baseDur !== null ? ` · ${baseDur}min` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-400">$</span>
                            <input
                              type="number"
                              defaultValue={q.custom_price}
                              onBlur={e => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v) && v !== q.custom_price) saveQualPrice(q, v);
                              }}
                              className="w-16 h-7 text-sm font-semibold text-slate-900 text-center border border-slate-200 rounded-md bg-[#FAFAF9] focus:border-rose-300 focus:outline-none"
                              data-testid={`price-${q.service_id}`}
                            />
                            <input
                              type="number"
                              defaultValue={effDur ?? ""}
                              placeholder={baseDur ?? ""}
                              onBlur={e => {
                                const raw = e.target.value;
                                const v = raw === "" ? null : parseInt(raw);
                                const current = q.custom_duration_minutes ?? null;
                                if (raw !== "" && isNaN(v)) return;
                                if (v !== current) saveQualDuration(q, v);
                              }}
                              className="w-14 h-7 text-sm font-semibold text-slate-900 text-center border border-slate-200 rounded-md bg-[#FAFAF9] focus:border-rose-300 focus:outline-none"
                              data-testid={`duration-${q.service_id}`}
                              title="Custom duration in minutes for this stylist"
                            />
                            <span className="text-[10px] text-slate-400">min</span>
                            <Button variant="ghost" size="sm" onClick={() => removeQualification(q.service_id)} className="text-rose-600 opacity-0 group-hover:opacity-100 p-1 h-auto" data-testid={`remove-qual-${q.service_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );})}
                    </div>
                  )}
                </div>

                {/* Emergency Contact */}
                {profile.emergency_contact && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Emergency Contact</p>
                    <div className="bg-[#FEF3CD] border border-[#FCEABB] rounded-lg p-3 text-sm">
                      <p className="font-medium text-[#856404]">{profile.emergency_contact}</p>
                      {profile.emergency_phone && <p className="text-xs text-[#856404]">{profile.emergency_phone}</p>}
                    </div>
                  </div>
                )}
              </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Add Services dialog (mounted only when open, so we fetch fresh state each time) */}
      {bulkServicesOpen && profile && (
        <BulkServicesForStaffDialog
          staff={profile}
          allServices={allServices}
          existingQualifications={qualifications}
          onClose={() => setBulkServicesOpen(false)}
          onDone={async () => {
            const res = await api.get(`/staff/${profile.user_id}/qualifications`);
            setQualifications(res.data || []);
          }}
        />
      )}

      {/* Owner-promotion manager-PIN gate. Only mounted while the dialog is open;
          submitting calls saveEdit(pin) which re-runs the save with X-Manager-Pin. */}
      <Dialog open={ownerPromoteOpen} onOpenChange={(v) => { if (!v) { setOwnerPromoteOpen(false); setOwnerPromotePin(""); setOwnerPromoteError(""); } }}>
        <DialogContent className="max-w-sm" data-testid="owner-promote-pin-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl border border-rose-100 bg-rose-50 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-rose-600" strokeWidth={2} />
              </div>
              <div>
                <DialogTitle className="font-['Outfit'] text-xl">Promote to Owner</DialogTitle>
                <DialogDescription className="text-xs font-['Manrope'] mt-1 leading-relaxed">
                  Enter the manager PIN to grant <span className="font-semibold text-slate-700">{profile?.name}</span> full owner access — billing, payroll, all staff records, and the ability to delete the workspace.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div>
            <label className="text-xs font-['Manrope'] text-slate-500 mb-1.5 block">Manager PIN</label>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              value={ownerPromotePin}
              onChange={(e) => { setOwnerPromotePin(e.target.value.replace(/\D/g, "")); setOwnerPromoteError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" && ownerPromotePin.length === 4) saveEdit(ownerPromotePin); }}
              placeholder="••••"
              className="text-center text-2xl tracking-[0.4em] font-['Outfit'] h-12 border-slate-200"
              data-testid="owner-promote-pin-input"
            />
            {ownerPromoteError && (
              <p className="text-xs text-rose-600 font-['Manrope'] mt-2" data-testid="owner-promote-pin-error">{ownerPromoteError}</p>
            )}
            <p className="text-[11px] text-slate-400 font-['Manrope'] mt-2.5 leading-relaxed">
              Don't have the PIN? Set or reset it in Settings → Manager PIN.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setOwnerPromoteOpen(false); setOwnerPromotePin(""); setOwnerPromoteError(""); }} data-testid="owner-promote-cancel">
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
              disabled={saving || ownerPromotePin.length !== 4}
              onClick={() => saveEdit(ownerPromotePin)}
              data-testid="owner-promote-confirm"
            >
              {saving ? "Promoting…" : "Promote to Owner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Staff Modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-[560px] bg-white border-slate-200 rounded-2xl p-8" data-testid="new-staff-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Name *</label><Input data-testid="staff-name-input" value={newStaff.name} onChange={e => setNewStaff(p => ({ ...p, name: e.target.value }))} className="h-10 border-slate-200" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Email *</label><Input data-testid="staff-email-input" value={newStaff.email} onChange={e => setNewStaff(p => ({ ...p, email: e.target.value }))} className="h-10 border-slate-200" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Phone</label><Input value={newStaff.phone} onChange={e => setNewStaff(p => ({ ...p, phone: e.target.value }))} className="h-10 border-slate-200" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Title</label><Input value={newStaff.title} onChange={e => setNewStaff(p => ({ ...p, title: e.target.value }))} className="h-10 border-slate-200" /></div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Role</label>
                <Select value={newStaff.role} onValueChange={v => setNewStaff(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="h-10 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stylist">Stylist</SelectItem><SelectItem value="colorist">Colorist</SelectItem>
                    <SelectItem value="esthetician">Esthetician</SelectItem><SelectItem value="nail_tech">Nail Tech</SelectItem>
                    <SelectItem value="educator">Educator</SelectItem>
                    <SelectItem value="front_desk">Front Desk</SelectItem><SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Location</label>
                <Select onValueChange={v => setNewStaff(p => ({ ...p, location_ids: [v] }))}>
                  <SelectTrigger className="h-10 border-slate-200"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Employment</label>
                <Select value={newStaff.employment_type} onValueChange={v => setNewStaff(p => ({ ...p, employment_type: v }))}>
                  <SelectTrigger className="h-10 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="full-time">Full-Time</SelectItem><SelectItem value="part-time">Part-Time</SelectItem><SelectItem value="contract">Contract</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Start Date</label><Input type="date" value={newStaff.start_date} onChange={e => setNewStaff(p => ({ ...p, start_date: e.target.value }))} className="h-10 border-slate-200" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Birthday</label><Input type="date" value={newStaff.birthday} onChange={e => setNewStaff(p => ({ ...p, birthday: e.target.value }))} className="h-10 border-slate-200" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Color</label><Input type="color" value={newStaff.color} onChange={e => setNewStaff(p => ({ ...p, color: e.target.value }))} className="h-10 border-slate-200 p-1" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Bio</label><textarea value={newStaff.bio} onChange={e => setNewStaff(p => ({ ...p, bio: e.target.value }))} className="w-full border border-slate-200 rounded-lg p-2 text-sm h-16 resize-none" placeholder="Short bio..." /></div>
            <div className="border-t border-slate-100 pt-3 space-y-1.5">
              <label className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-amber-600" />
                Initial Login Password <span className="text-[11px] text-slate-400 font-normal">(optional)</span>
              </label>
              <Input
                data-testid="staff-initial-password"
                type="password"
                value={newStaff.initial_password}
                onChange={e => setNewStaff(p => ({ ...p, initial_password: e.target.value }))}
                placeholder="Min 8 characters — leave blank to send an email invite later"
                className="h-10 border-slate-200"
              />
              <p className="text-[11px] text-slate-400 font-['Manrope']">
                Set a password now so {newStaff.name?.split(" ")[0] || "this team member"} can sign in immediately, or leave blank and click "Reset Password → Email a link" on their profile later.
              </p>
            </div>
            <Button data-testid="create-staff-btn" onClick={handleCreate} className="w-full bg-slate-900 text-white rounded-full h-11">Add Team Member</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete with Manager PIN */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(v) => { setBulkDeleteOpen(v); if (!v) setBulkPin(""); }}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl border border-rose-100 bg-rose-50 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" strokeWidth={2} />
              </div>
              <div>
                <DialogTitle className="font-['Outfit'] text-xl">Delete {selectedIds.size} team member{selectedIds.size === 1 ? "" : "s"}?</DialogTitle>
                <DialogDescription className="text-xs font-['Manrope']">
                  This permanently removes the selected staff and all their associated data (shifts, timeclock entries, qualifications). Future appointments will be unassigned.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 max-h-44 overflow-auto">
            <p className="text-[10px] font-bold uppercase tracking-widest text-rose-600 mb-1.5 font-['Manrope']">Selected</p>
            <ul className="space-y-1 text-[13px] text-rose-900 font-['Manrope']" data-testid="bulk-delete-list">
              {staff.filter(s => selectedIds.has(s.user_id)).map(s => (
                <li key={s.user_id} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ backgroundColor: s.color || "#B5A493" }}>
                    {s.name?.[0]}
                  </div>
                  <span>{s.name}</span>
                  <span className="text-[10px] text-rose-500 uppercase tracking-wider">{s.role?.replace("_", " ")}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block font-['Manrope']">Manager PIN</label>
            <Input data-testid="bulk-delete-pin" type="password" inputMode="numeric" maxLength={8}
              placeholder="4-digit PIN"
              value={bulkPin}
              onChange={e => setBulkPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => { if (e.key === "Enter" && bulkPin.length >= 4) confirmBulkDelete(); }}
              autoFocus
              className="h-10 border-slate-200 font-mono tracking-widest" />
            <p className="text-[11px] text-slate-400 font-['Manrope'] mt-1.5">Set in Command Center → Manager PIN</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={confirmBulkDelete} disabled={bulkDeleting || bulkPin.length < 4}
              data-testid="bulk-delete-confirm"
              className="bg-rose-600 text-white rounded-full hover:bg-rose-700">
              {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size} team member${selectedIds.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner-only Password Reset */}
      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) { setResetPwd(""); setResetPwd2(""); setEmailLinkResult(null); setResetMode("manual"); setManualResetSuccess(null); setShowResetPwd(false); setOwnerResetAck(false); } }}>
        <DialogContent className="max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl border border-amber-100 bg-amber-50 flex items-center justify-center flex-shrink-0">
                <KeyRound className="w-5 h-5 text-amber-600" strokeWidth={2} />
              </div>
              <div>
                <DialogTitle className="font-['Outfit'] text-xl">Reset password</DialogTitle>
                <DialogDescription className="text-xs font-['Manrope']">
                  Reset password for <span className="font-semibold text-slate-700">{profile?.name}</span>.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Emergency-owner reset banner: shown only when current owner is resetting *another* owner's password.
              Requires explicit ack checkbox before the action buttons enable. */}
          {!manualResetSuccess && profile?.role === "owner" && profile?.user_id !== currentUser?.user_id && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-[12px] font-['Manrope']" data-testid="owner-reset-warning-banner">
              <p className="font-semibold text-rose-700 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> Emergency: you're resetting another owner's password
              </p>
              <p className="text-rose-600/80 mt-1 leading-relaxed">
                {profile?.name?.split(" ")[0]} has full access to billing, payroll, and every client record. Their existing sessions on every device will be revoked the moment you save. Use this only when they've been locked out, lost their device, or as a security precaution.
              </p>
              <label className="flex items-start gap-2 mt-2.5 cursor-pointer" data-testid="owner-reset-ack-row">
                <input
                  type="checkbox"
                  checked={ownerResetAck}
                  onChange={(e) => setOwnerResetAck(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-rose-600"
                  data-testid="owner-reset-ack-checkbox"
                />
                <span className="text-[12px] text-rose-700 leading-snug">
                  I confirm this is an emergency reset and {profile?.name?.split(" ")[0]} is aware (or unreachable).
                </span>
              </label>
            </div>
          )}

          {/* Success confirmation card after a manual reset */}
          {manualResetSuccess ? (
            <div className="space-y-3 pt-2" data-testid="reset-success-card">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-emerald-900 font-['Manrope']">
                      Password updated for {manualResetSuccess.name}
                    </p>
                    <p className="text-[11px] text-emerald-700 mt-0.5 font-['Manrope']">
                      The new password is active right now. Old passwords no longer work.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-3.5 space-y-2">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Email</p>
                  <p className="text-[13px] text-slate-700 font-['Manrope'] break-all" data-testid="reset-success-email">{manualResetSuccess.email || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">New Password</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="flex-1 text-[13px] font-mono bg-white border border-slate-200 rounded-md px-2 py-1.5 text-slate-800 break-all" data-testid="reset-success-password">
                      {manualResetSuccess.password}
                    </code>
                    <Button variant="outline" size="sm"
                      onClick={() => { navigator.clipboard.writeText(manualResetSuccess.password); toast.success("Password copied"); }}
                      data-testid="copy-new-password-btn"
                      className="h-9 rounded-full text-[11px]">
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                <p className="text-[11px] text-amber-800 font-['Manrope']">
                  <span className="font-semibold">Tell {manualResetSuccess.name?.split(" ")[0] || "them"} to log out everywhere first</span>, then log back in with this password. If they're typing it on a phone, autofill may insert the old password — have them clear the field and type it fresh.
                </p>
              </div>
            </div>
          ) : (
            <>
          {/* Mode toggle */}
          <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-full" data-testid="reset-mode-toggle">
            <button onClick={() => { setResetMode("manual"); setEmailLinkResult(null); }}
              data-testid="mode-manual"
              className={`flex-1 py-1.5 px-3 rounded-full text-[12px] font-semibold transition ${resetMode === "manual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Set manually
            </button>
            <button onClick={() => { setResetMode("email"); setEmailLinkResult(null); }}
              data-testid="mode-email"
              className={`flex-1 py-1.5 px-3 rounded-full text-[12px] font-semibold transition ${resetMode === "email" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Email a link
            </button>
          </div>

          {resetMode === "manual" ? (
            <div className="space-y-3 pt-2">
              <p className="text-[12px] text-slate-500 font-['Manrope']">
                Type a new password. {profile?.name?.split(" ")[0]} will need it to log in.
              </p>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block font-['Manrope']">New Password</label>
                <div className="relative">
                  <Input type={showResetPwd ? "text" : "password"} value={resetPwd} onChange={e => setResetPwd(e.target.value)}
                    placeholder="Minimum 8 characters" autoFocus
                    data-testid="reset-password-input"
                    className="h-10 border-slate-200 pr-10" />
                  <button type="button" onClick={() => setShowResetPwd(s => !s)}
                    data-testid="toggle-show-password"
                    aria-label={showResetPwd ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700">
                    {showResetPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block font-['Manrope']">Confirm Password</label>
                <Input type={showResetPwd ? "text" : "password"} value={resetPwd2} onChange={e => setResetPwd2(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="h-10 border-slate-200" />
              </div>
              {resetPwd && resetPwd2 && resetPwd !== resetPwd2 && (
                <p className="text-[11px] text-rose-600 font-['Manrope']">Passwords don't match</p>
              )}
            </div>
          ) : (
            <div className="space-y-3 pt-2" data-testid="reset-mode-email-panel">
              {!emailLinkResult ? (
                <>
                  <p className="text-[12px] text-slate-500 font-['Manrope']">
                    Send a secure one-time reset link to <span className="font-semibold text-slate-700">{profile?.email}</span>. The link expires in 24 hours.
                  </p>
                  <Button onClick={sendResetLink} disabled={resetSaving || !profile?.email || (profile?.role === "owner" && profile?.user_id !== currentUser?.user_id && !ownerResetAck)}
                    data-testid="send-reset-link-btn"
                    className="w-full h-10 rounded-full bg-slate-900 text-white hover:bg-slate-800">
                    {resetSaving ? "Sending…" : "Send reset link"}
                  </Button>
                  {!profile?.email && (
                    <p className="text-[11px] text-rose-600 font-['Manrope']">No email on file — use "Set manually" instead.</p>
                  )}
                </>
              ) : (
                <div className="space-y-3" data-testid="reset-link-result">
                  {emailLinkResult.email_sent ? (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[12px] font-semibold text-emerald-900">Email sent to {profile.email}</p>
                          <p className="text-[11px] text-emerald-700 mt-0.5">Link expires in 24 hours.</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <Mail className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[12px] font-semibold text-amber-900">Email not configured</p>
                          <p className="text-[11px] text-amber-700 mt-0.5">Copy the link below and share it with {profile?.name?.split(" ")[0]} directly. Configure SendGrid in Settings → Integrations to auto-send next time.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 font-['Manrope']">Reset Link</p>
                    <p className="text-[11px] text-slate-700 break-all font-mono leading-relaxed" data-testid="reset-link-url">{emailLinkResult.reset_url}</p>
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(emailLinkResult.reset_url); toast.success("Link copied"); }}
                      data-testid="copy-reset-link"
                      className="mt-2 h-7 text-[11px] rounded-full">
                      Copy link
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} className="rounded-full" data-testid="reset-dialog-close">
              {(emailLinkResult || manualResetSuccess) ? "Done" : "Cancel"}
            </Button>
            {!manualResetSuccess && resetMode === "manual" && (
              <Button onClick={resetStaffPassword} disabled={resetSaving || !resetPwd || resetPwd !== resetPwd2 || (profile?.role === "owner" && profile?.user_id !== currentUser?.user_id && !ownerResetAck)}
                data-testid="reset-password-submit"
                className="bg-slate-900 text-white rounded-full hover:bg-slate-800">
                {resetSaving ? "Saving…" : "Reset password"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Sessions / Force log-out everywhere */}
      <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <DialogContent className="max-w-md" data-testid="revoke-sessions-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                <LogOut className="w-5 h-5 text-slate-600" strokeWidth={2} />
              </div>
              <div>
                <DialogTitle className="font-['Outfit'] text-xl">
                  {profile?.user_id === currentUser?.user_id ? "Sign out everywhere else?" : `Sign ${profile?.name?.split(" ")[0] || "this user"} out everywhere?`}
                </DialogTitle>
                <DialogDescription className="text-xs font-['Manrope']">
                  {profile?.user_id === currentUser?.user_id
                    ? "Every other phone, tablet, or browser you're signed into will be logged out on its next request. This window stays signed in."
                    : <>Every device <span className="font-semibold text-slate-700">{profile?.name}</span> is signed into will be kicked out on its next request. They'll need to type their password again. Use this if a device was lost, stolen, or after suspicious activity.</>}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-[12px] text-amber-800 font-['Manrope']">
            <span className="font-semibold">Tip:</span> resetting a password already does this automatically — only use this when you don't want to change the password.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOpen(false)} className="rounded-full" data-testid="revoke-cancel-btn">Cancel</Button>
            <Button onClick={revokeSessions} disabled={revokeSaving}
              data-testid="confirm-revoke-btn"
              className="bg-slate-900 text-white rounded-full hover:bg-slate-800">
              {revokeSaving ? "Signing out…" : (profile?.user_id === currentUser?.user_id ? "Sign out other devices" : "Force sign out")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Deactivate / Delete Confirmation with Manager PIN */}
      <Dialog open={deletePromptOpen} onOpenChange={(v) => { setDeletePromptOpen(v); if (!v) { setDeletePin(""); setDeleteError(""); setDeleteMode("deactivate"); } }}>
        <DialogContent className="max-w-md" data-testid="delete-staff-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center flex-shrink-0 ${
                deleteMode === "delete" ? "bg-rose-50 border-rose-100" : "bg-amber-50 border-amber-100"
              }`}>
                <Trash2 className={`w-5 h-5 ${deleteMode === "delete" ? "text-rose-600" : "text-amber-600"}`} strokeWidth={2} />
              </div>
              <div>
                <DialogTitle className="font-['Outfit'] text-lg tracking-tight">
                  Remove {profile?.name}?
                </DialogTitle>
                <p className="text-xs text-slate-500 font-['Manrope'] mt-1 leading-relaxed">
                  Pick what should happen to their record. Deactivation is recommended — it preserves history for taxes, disputes, and payroll audits.
                </p>
              </div>
            </div>
          </DialogHeader>

          {profile?.role === "owner" && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-[12px] font-['Manrope']" data-testid="owner-warning-banner">
              <p className="font-semibold text-rose-700">You're removing another owner.</p>
              <p className="text-rose-600/80 mt-0.5 leading-relaxed">
                Owners have full access to billing, payroll, and every client record. Make sure this is intended. Last-owner removal is blocked automatically — at least one active owner must always remain.
              </p>
            </div>
          )}

          <div className="mt-4 space-y-2" data-testid="delete-mode-group">
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
              deleteMode === "deactivate" ? "border-amber-300 bg-amber-50/40 shadow-sm" : "border-slate-200 hover:border-slate-300"
            }`}>
              <input type="radio" name="delete-mode" value="deactivate"
                checked={deleteMode === "deactivate"}
                onChange={() => setDeleteMode("deactivate")}
                data-testid="mode-deactivate"
                className="mt-0.5 text-amber-500 focus:ring-amber-400" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900 font-['Outfit']">Deactivate</p>
                  <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-['Manrope']">Recommended</span>
                </div>
                <p className="text-[11px] text-slate-500 font-['Manrope'] mt-0.5 leading-relaxed">Hides from roster but keeps history intact. Future appointments get unassigned. Fully reversible.</p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
              deleteMode === "delete" ? "border-rose-300 bg-rose-50/40 shadow-sm" : "border-slate-200 hover:border-slate-300"
            }`}>
              <input type="radio" name="delete-mode" value="delete"
                checked={deleteMode === "delete"}
                onChange={() => setDeleteMode("delete")}
                data-testid="mode-delete"
                className="mt-0.5 text-rose-500 focus:ring-rose-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900 font-['Outfit']">Delete Forever</p>
                <p className="text-[11px] text-slate-500 font-['Manrope'] mt-0.5 leading-relaxed">Permanently wipes the account and all associated data (time-clock, commissions, training, goals, messages). <span className="text-rose-600 font-semibold">Cannot be undone.</span></p>
              </div>
            </label>
          </div>

          <div className="mt-4">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block font-['Manrope']">Enter manager PIN to confirm</label>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={deletePin}
              onChange={e => { setDeleteError(""); setDeletePin(e.target.value.replace(/\D/g, "").slice(0, 8)); }}
              onKeyDown={e => { if (e.key === "Enter") confirmDelete(); }}
              placeholder="• • • •"
              data-testid="delete-manager-pin"
              className="w-full h-12 px-4 rounded-xl border border-slate-200 text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:border-rose-400 tabular-nums"
            />
            {deleteError && <p className="text-rose-600 text-xs mt-2 font-['Manrope']" data-testid="delete-error">{deleteError}</p>}
          </div>

          <div className="flex items-center gap-2 mt-5">
            <Button variant="outline" onClick={() => setDeletePromptOpen(false)}
              data-testid="delete-cancel-btn"
              className="flex-1 border-slate-200 rounded-full h-10 text-sm">
              Cancel
            </Button>
            <Button onClick={confirmDelete} disabled={deleting || deletePin.length < 4}
              data-testid="delete-confirm-btn"
              className={`flex-1 text-white rounded-full h-10 text-sm font-semibold disabled:opacity-40 ${
                deleteMode === "delete" ? "bg-rose-500 hover:bg-rose-600" : "bg-amber-500 hover:bg-amber-600"
              }`}>
              {deleting ? (deleteMode === "delete" ? "Deleting…" : "Deactivating…") : (deleteMode === "delete" ? "Delete Forever" : "Deactivate")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
